import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {StoreSelectorsService} from "./store-selectors.service";
import {IGroupCollection, IGroupCollectionDetails, PrivilegeModules, PrivilegeModulesProperties, PrivilegeModulesTable} from "../interfaces/enterprise-interface";
import {debounceTime, distinctUntilChanged, map, take} from "rxjs/operators";
import {Lib} from "../../libs/Lib";
import {Compbaser} from "../components/compbaser";
import {compareIds, isNullOrUndefOrEmpty} from "../common";
import {actionRemoveEventParam, actionRemoveMusicChannel, actionRemoveResources, actionRemoveScenes} from "../store/actions/user.actions";
import {IAccessTable, IUser} from "../interfaces";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";

@Injectable()
export class AccessService extends Compbaser {

    m_user: IUser;

    constructor(private storeSelectorsService: StoreSelectorsService, private toastr: ToastrService, private translocoService: TranslocoService) {
        super();
        this.initAccountRestrictions();
    }

    private initAccountRestrictions() {
        this.cod(
            this.storeSelectorsService
                .getUserAccessMask()
                .subscribe((maskDecoded) => {
                    this.removeScenes(maskDecoded);
                    this.removeResources(maskDecoded);
                    this.removeMusic(maskDecoded);
                    this.removeEventParams(maskDecoded);
                })
        );
    }

    public getGroupsTable(i_module: PrivilegeModules, i_tableName: PrivilegeModulesTable): Observable<IAccessTable> {
        const fullAccess = {upd: true, add: true, del: true};
        return this.storeSelectorsService.getGroupsPrivileges()
            .pipe(
                map((v) => {
                    if (!v?.Group) {
                        /** possibly user did not login so we just default **/
                        return fullAccess;
                    }
                    const r = v.Group.findIndex((x) => x.$.name == i_module);
                    /** did not find the module as it must have been added recently (like 'SignStudio editors' for example that was added in 2022)
                     and so if we don't find the module we just give full access which is fine since this user is not under a reseller, since
                     if he was under a reseller, his reseller would have already created a new set of privileges which would have had ALL the latest modules / tables
                     **/
                    if (r === -1) {
                        // Lib.DevShow(`ACL: found new module ${i_module} allowing full access`);
                        return fullAccess;
                    }
                    const a = Lib.GetAccessMask3bit(Number(v.Group[r].Tables[0].$[i_tableName]));
                    if (isNullOrUndefOrEmpty(r)) {
                        Lib.DevShow('ACL: no privileges so defaulting to all access');
                        return fullAccess;
                    }
                    return {upd: a[0], add: a[1], del: a[2]};
                }));
    }

    /** accessService.getGroupsProperties("Stations", 'lanServer') or accessService.getGroupsProperties("Stations") **/
    public getGroupsProperties(i_module: PrivilegeModules, i_propertyName?: PrivilegeModulesProperties): Observable<string | IGroupCollectionDetails> {
        return this.storeSelectorsService.getGroupsPrivileges()
            .pipe(
                map((v) => {
                    const r = v.Group.findIndex((x) => x.$.name == i_module);
                    if (i_propertyName) {
                        return v.Group[r].$[i_propertyName];
                    } else {
                        return v.Group[r].$;
                    }
                }));
    }

    public getGroupsCollection(i_module: PrivilegeModules): Observable<IGroupCollection> {
        return this.storeSelectorsService.getGroupsPrivileges()
            .pipe(
                debounceTime(100),
                distinctUntilChanged((a, b) => {
                    return JSON.stringify(a) === JSON.stringify(b);
                }),
                map((v) => {
                    if (isNullOrUndefOrEmpty(v)) {
                        console.log('getGroupsCollection: did not find group info');
                        return null;
                    }
                    const r = v.Group.findIndex((x) => x.$.name == i_module);
                    /** didn't find the group so must have been recently added XML module / new group **/
                    if (r === -1) {
                        return null;
                    }
                    return v.Group[r];
                }));
    }

    private removeMusic(maskTemplate: Array<string>) {
        this.storeSelectorsService.getMusicChannels()
            .pipe(take(1))
            .subscribe((items) => {
                items.forEach((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.access_key)) == -1) {
                        this.consoleRemovedFromStore('music', s.music_channel_id);
                        this.storeSelectorsService.dispatch(actionRemoveMusicChannel({music_channel_id: s.music_channel_id}));
                    }
                });
            });
    }

    private removeResources(maskTemplate: Array<string>) {
        this.storeSelectorsService.getResources()
            .pipe(take(1))
            .subscribe((items) => {
                const ids = [];
                items.filter((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.access_key)) == -1) {
                        this.consoleRemovedFromStore('resource', s.resource_name);
                        ids.push(s.resource_id);
                    }
                });
                this.storeSelectorsService.dispatch(actionRemoveResources({resourceIds: ids}));
            });
    }


    private removeEventParams(maskTemplate: Array<string>) {
        this.storeSelectorsService.getEventParams()
            .pipe(take(1))
            .subscribe((items) => {
                items.forEach((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.access_key)) == -1) {
                        this.consoleRemovedFromStore('eventParam', s.event_param_id);
                        this.storeSelectorsService.dispatch(actionRemoveEventParam({eventParamId: s.event_param_id}));
                    }
                });
            });
    }

    private consoleRemovedFromStore(i_from: string, i_ids) {
        // if (Lib.DevMode()) {
        //      console.log(`access removal ${i_from} ${i_ids}`);
        // }
    }

    private removeScenes(maskTemplate: Array<string>) {
        this.storeSelectorsService.getScenes(true)
            .pipe(take(1))
            .subscribe((items) => {
                const ids = [];
                items.forEach((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.Player.$.accessKey)) == -1) {
                        ids.push(s.Player.$.id);
                    }
                });
                this.consoleRemovedFromStore('scenes', ids);
                this.storeSelectorsService.dispatch(actionRemoveScenes({sceneIds: ids}));
            });
    }

    public showNoAccess() {
        this.toastr.clear();
        return this.toastr.error(this.translocoService.translate('toast.noPermissionUpgEnterprise', {}, this.translocoService.getActiveLang()));
    }
}

